// Open-on-tap interaction for the album preview (FIX 1): "tap twice" everywhere
// — double-tap on touch, double-click on desktop. Requiring two taps keeps the
// gesture intentional (a stray scroll-tap or single click never opens the
// editor) and matches the on-screen hint shown in the preview.
import { useRef } from 'react';
import type { PointerEvent } from 'react';

/** Pure: does a tap at `nowTs` (ms) form a double-tap with the previous tap at
 *  `prevTs`, within `windowMs`? `prevTs <= 0` means "no prior tap recorded". */
export function isDoubleTap(prevTs: number, nowTs: number, windowMs = 300): boolean {
  if (prevTs <= 0) return false;
  const delta = nowTs - prevTs;
  return delta > 0 && delta <= windowMs;
}

/**
 * Returns event-handler props to spread on a tappable element so it opens
 * `onOpen` on single-click (fine/mouse pointer) or double-tap (coarse/touch).
 * Returns an empty object when `onOpen` is undefined (element stays inert).
 */
export function useTapToOpen(onOpen?: () => void): {
  onDoubleClick?: () => void;
  onPointerUp?: (e: PointerEvent) => void;
} {
  const lastTapRef = useRef(0);
  if (!onOpen) return {};
  const finePointer =
    typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: fine)')?.matches;
  return {
    // Desktop / mouse: native double-click opens. (Guarded to fine pointers so
    // a touch double-tap doesn't also fire this and open twice.)
    onDoubleClick: () => {
      if (finePointer) onOpen();
    },
    // Touch: detect a double-tap manually (onDoubleClick is unreliable on mobile
    // browsers). A single tap just records the time and does nothing.
    onPointerUp: (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return; // mouse handled by onDoubleClick
      const now = e.timeStamp || Date.now();
      if (isDoubleTap(lastTapRef.current, now)) {
        lastTapRef.current = 0;
        onOpen();
      } else {
        lastTapRef.current = now;
      }
    },
  };
}
